import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { 
  Camera, Video, Upload, AlertTriangle, Shield, Eye, 
  Play, Pause, RefreshCw, Settings, Bell, CheckCircle,
  XCircle, Clock, MapPin, Activity, Zap, ChevronRight,
  Image as ImageIcon, Loader2, AlertCircle, TrendingUp,
  Users, HardHat, Flame, Droplets, Wrench, Mail, Plus, Trash2,
  ToggleLeft, ToggleRight, Send, History
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { WsafeReviewStatus } from '../components/WsafeReviewStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CCTVMonitoringPage = () => {
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('monitor');
  const [feeds, setFeeds] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedFeed, setSelectedFeed] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // New feed form
  const [newFeed, setNewFeed] = useState({
    name: '',
    location: '',
    description: ''
  });

  // Alert configuration state
  const [alertConfigs, setAlertConfigs] = useState([]);
  const [alertNotifications, setAlertNotifications] = useState([]);
  const [showNewConfig, setShowNewConfig] = useState(false);
  const [newConfig, setNewConfig] = useState({
    name: 'CCTV Safety Alerts',
    enabled: true,
    severity_triggers: ['critical', 'high', 'medium'],
    notification_channels: ['email', 'in_app'],
    contacts: [{ name: '', email: '', role: '' }],
    cooldown_minutes: 15,
    alert_on_types: ['ppe_violation', 'unsafe_behavior', 'hazard_detected', 'fall_risk', 'equipment_issue']
  });

  // Live simulation state
  const [simulatingLive, setSimulatingLive] = useState(false);
  const [liveResult, setLiveResult] = useState(null);

  // Continuous monitoring state
  const [continuousMode, setContinuousMode] = useState(false);
  const [monitoringInterval, setMonitoringInterval] = useState(10); // seconds
  const [monitoringLog, setMonitoringLog] = useState([]);
  const [analysisCount, setAnalysisCount] = useState(0);
  const [hazardsFound, setHazardsFound] = useState(0);
  const continuousIntervalRef = useRef(null);

  // Image Loop Live Feed state
  const [sampleImages, setSampleImages] = useState([]);
  const [currentFrame, setCurrentFrame] = useState(null);
  const [livePlayback, setLivePlayback] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(3); // seconds between frames
  const [uploadingSamples, setUploadingSamples] = useState(false);
  const [analyzingFrame, setAnalyzingFrame] = useState(false);
  const sampleUploadRef = useRef(null);
  const livePlaybackRef = useRef(null);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  // Continuous monitoring effect
  useEffect(() => {
    if (continuousMode && selectedFeed) {
      // Start continuous monitoring
      continuousIntervalRef.current = setInterval(async () => {
        await runContinuousAnalysis();
      }, monitoringInterval * 1000);

      // Run immediately
      runContinuousAnalysis();

      return () => {
        if (continuousIntervalRef.current) {
          clearInterval(continuousIntervalRef.current);
        }
      };
    } else {
      // Stop continuous monitoring
      if (continuousIntervalRef.current) {
        clearInterval(continuousIntervalRef.current);
        continuousIntervalRef.current = null;
      }
    }
  }, [continuousMode, selectedFeed, monitoringInterval]);

  const runContinuousAnalysis = async () => {
    if (!selectedFeed || simulatingLive || analyzingFrame) return;

    // Check if we have sample images for real AI analysis
    if (sampleImages.length === 0) {
      // No sample images - show toast only once and stop
      if (analysisCount === 0) {
        toast({
          title: "No Sample Images",
          description: "Upload sample images to enable real-time AI monitoring",
          variant: "destructive"
        });
      }
      setContinuousMode(false);
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');

      // Use real AI analysis endpoint instead of simulation
      const response = await axios.post(`${API_URL}/api/cctv-monitoring/feeds/${selectedFeed.id}/analyze-current-frame`, {}, {
        headers: { 
          Authorization: `Bearer ${token}`
        }
      });

      const result = response.data;
      setLiveResult(result);
      setAnalysisCount(prev => prev + 1);

      // Update current frame with the analyzed image
      if (result.frame_info) {
        setCurrentFrame({
          image_data: result.frame_info.image_data,
          image_id: result.frame_info.image_id,
          filename: result.frame_info.filename,
          index: result.frame_info.index,
          total_images: result.frame_info.total_images
        });
      }

      // Add to monitoring log (keep last 20)
      const logEntry = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        feedName: result.feed_name,
        hazardsDetected: result.analysis?.hazards_detected || false,
        severity: result.analysis?.severity,
        alertType: result.analysis?.alert_type,
        description: result.analysis?.description?.substring(0, 100),
        frameIndex: result.frame_info?.index,
        isRealAI: true
      };
      
      setMonitoringLog(prev => [logEntry, ...prev].slice(0, 20));

      if (result.alert) {
        setHazardsFound(prev => prev + 1);
        toast({
          title: "⚠️ Hazard Detected by AI!",
          description: result.alert.description,
          variant: "destructive"
        });
        // Refresh alerts
        fetchData();
      }
    } catch (error) {
      console.error('Real-time AI analysis error:', error);
      // Only show error on first failure
      if (analysisCount === 0) {
        toast({
          title: "Analysis Failed",
          description: error.response?.data?.detail || "Failed to run AI analysis",
          variant: "destructive"
        });
      }
    }
  };

  const toggleContinuousMode = () => {
    if (!selectedFeed) {
      toast({ title: "Error", description: "Please select a camera first", variant: "destructive" });
      return;
    }

    if (sampleImages.length === 0) {
      toast({ 
        title: "No Sample Images", 
        description: "Upload sample images first to enable real-time AI monitoring",
        variant: "destructive" 
      });
      return;
    }
    
    if (continuousMode) {
      // Stopping
      setContinuousMode(false);
      toast({ title: "AI Monitoring Stopped", description: `Analyzed ${analysisCount} frames with real AI, found ${hazardsFound} hazards` });
    } else {
      // Starting
      setAnalysisCount(0);
      setHazardsFound(0);
      setMonitoringLog([]);
      setContinuousMode(true);
      toast({ 
        title: "🤖 Real-Time AI Monitoring Started", 
        description: `Analyzing ${selectedFeed.name} every ${monitoringInterval}s using GPT-4o AI` 
      });
    }
  };

  // ============== IMAGE LOOP LIVE FEED FUNCTIONS ==============

  // Fetch sample images when feed is selected
  useEffect(() => {
    if (selectedFeed?.id) {
      fetchSampleImages(selectedFeed.id);
    } else {
      setSampleImages([]);
      setCurrentFrame(null);
    }
  }, [selectedFeed?.id]);

  // Live playback effect
  useEffect(() => {
    if (livePlayback && selectedFeed && sampleImages.length > 0) {
      livePlaybackRef.current = setInterval(async () => {
        await fetchCurrentFrame();
      }, playbackSpeed * 1000);

      // Fetch immediately
      fetchCurrentFrame();

      return () => {
        if (livePlaybackRef.current) {
          clearInterval(livePlaybackRef.current);
        }
      };
    } else {
      if (livePlaybackRef.current) {
        clearInterval(livePlaybackRef.current);
        livePlaybackRef.current = null;
      }
    }
  }, [livePlayback, selectedFeed, sampleImages.length, playbackSpeed]);

  const fetchSampleImages = async (feedId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/cctv-monitoring/feeds/${feedId}/sample-images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSampleImages(response.data.images || []);
    } catch (error) {
      setSampleImages([]);
    }
  };

  const uploadSampleImages = async (event) => {
    const files = event.target.files;
    if (!files.length || !selectedFeed) return;

    setUploadingSamples(true);
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      
      for (let i = 0; i < files.length; i++) {
        formData.append('images', files[i]);
      }

      await axios.post(`${API_URL}/api/cctv-monitoring/feeds/${selectedFeed.id}/sample-images`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({ title: "Success", description: `Uploaded ${files.length} sample image(s)` });
      await fetchSampleImages(selectedFeed.id);
    } catch (error) {
      toast({ title: "Error", description: error?.response?.status === 403 ? "Admin role required" : "Failed to upload images", variant: "destructive" });
    } finally {
      setUploadingSamples(false);
      if (sampleUploadRef.current) sampleUploadRef.current.value = '';
    }
  };

  const deleteSampleImage = async (imageId) => {
    if (!selectedFeed) return;

    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API_URL}/api/cctv-monitoring/feeds/${selectedFeed.id}/sample-images/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Image removed" });
      await fetchSampleImages(selectedFeed.id);
      setCurrentFrame(null);
    } catch (error) {
      toast({ title: "Error", description: error?.response?.status === 403 ? "Admin role required" : "Failed to delete image", variant: "destructive" });
    }
  };

  const clearAllSampleImages = async () => {
    if (!selectedFeed) return;

    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API_URL}/api/cctv-monitoring/feeds/${selectedFeed.id}/sample-images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "All sample images cleared" });
      setSampleImages([]);
      setCurrentFrame(null);
      setLivePlayback(false);
    } catch (error) {
      toast({ title: "Error", description: error?.response?.status === 403 ? "Admin role required" : "Failed to clear images", variant: "destructive" });
    }
  };

  const fetchCurrentFrame = async () => {
    if (!selectedFeed) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/cctv-monitoring/feeds/${selectedFeed.id}/current-frame`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.has_frame) {
        setCurrentFrame(response.data.frame);
      }
    } catch (error) {
      // Silent fail for frame fetch
    }
  };

  const analyzeCurrentFrame = async () => {
    if (!selectedFeed || sampleImages.length === 0) {
      toast({ title: "Error", description: "No sample images available", variant: "destructive" });
      return;
    }

    setAnalyzingFrame(true);
    setLiveResult(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API_URL}/api/cctv-monitoring/feeds/${selectedFeed.id}/analyze-current-frame`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLiveResult(response.data);
      
      // Update current frame with the analyzed image
      if (response.data.frame_info) {
        setCurrentFrame({
          image_data: response.data.frame_info.image_data,
          image_id: response.data.frame_info.image_id,
          filename: response.data.frame_info.filename,
          index: response.data.frame_info.index,
          total_images: response.data.frame_info.total_images
        });
      }

      if (response.data.alert) {
        toast({
          title: "⚠️ Safety Alert Generated!",
          description: response.data.alert.description,
          variant: "destructive"
        });
        fetchData();
      } else {
        toast({
          title: "✓ All Clear",
          description: "No safety hazards detected"
        });
      }
    } catch (error) {
      toast({
        title: "Analysis Failed",
        description: error.response?.data?.detail || "Failed to analyze frame",
        variant: "destructive"
      });
    } finally {
      setAnalyzingFrame(false);
    }
  };

  const toggleLivePlayback = () => {
    if (sampleImages.length === 0) {
      toast({ title: "Error", description: "Upload sample images first", variant: "destructive" });
      return;
    }
    setLivePlayback(!livePlayback);
  };

  // ============== END IMAGE LOOP FUNCTIONS ==============

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [feedsRes, alertsRes, statsRes, configsRes, notificationsRes] = await Promise.all([
        axios.get(`${API_URL}/api/cctv-monitoring/feeds`, { headers }).catch(() => ({ data: { feeds: [] } })),
        axios.get(`${API_URL}/api/cctv-monitoring/alerts`, { headers }).catch(() => ({ data: { alerts: [] } })),
        axios.get(`${API_URL}/api/cctv-monitoring/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/cctv-monitoring/alert-config`, { headers }).catch(() => ({ data: { configs: [] } })),
        axios.get(`${API_URL}/api/cctv-monitoring/alert-notifications`, { headers }).catch(() => ({ data: { notifications: [] } }))
      ]);
      
      setFeeds(feedsRes.data.feeds || []);
      setAlerts(alertsRes.data.alerts || []);
      setStats(statsRes.data || {});
      setAlertConfigs(configsRes.data.configs || []);
      setAlertNotifications(notificationsRes.data.notifications || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Alert configuration functions
  const createAlertConfig = async () => {
    if (!newConfig.name || newConfig.contacts.filter(c => c.email).length === 0) {
      toast({ title: "Error", description: "Please provide a name and at least one contact email", variant: "destructive" });
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API_URL}/api/cctv-monitoring/alert-config`, newConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Success", description: "Alert configuration created" });
      setShowNewConfig(false);
      setNewConfig({
        name: 'CCTV Safety Alerts',
        enabled: true,
        severity_triggers: ['critical', 'high', 'medium'],
        notification_channels: ['email', 'in_app'],
        contacts: [{ name: '', email: '', role: '' }],
        cooldown_minutes: 15,
        alert_on_types: ['ppe_violation', 'unsafe_behavior', 'hazard_detected', 'fall_risk', 'equipment_issue']
      });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error?.response?.status === 403 ? "Admin role required" : "Failed to create configuration", variant: "destructive" });
    }
  };

  const toggleAlertConfig = async (configId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API_URL}/api/cctv-monitoring/alert-config/${configId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error?.response?.status === 403 ? "Admin role required" : "Failed to toggle configuration", variant: "destructive" });
    }
  };

  const deleteAlertConfig = async (configId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API_URL}/api/cctv-monitoring/alert-config/${configId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Configuration deleted" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error?.response?.status === 403 ? "Admin role required" : "Failed to delete configuration", variant: "destructive" });
    }
  };

  const testAlertConfig = async (configId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API_URL}/api/cctv-monitoring/alert-config/${configId}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ 
        title: "Test Alert Sent", 
        description: `Email: ${response.data.email_sent ? 'Sent' : 'Not sent'}, In-App: ${response.data.in_app_notification ? 'Created' : 'Not created'}` 
      });
    } catch (error) {
      toast({ title: "Error", description: error?.response?.status === 403 ? "Admin role required" : "Failed to send test alert", variant: "destructive" });
    }
  };

  const addContact = () => {
    setNewConfig(prev => ({
      ...prev,
      contacts: [...prev.contacts, { name: '', email: '', role: '' }]
    }));
  };

  const removeContact = (index) => {
    setNewConfig(prev => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== index)
    }));
  };

  const updateContact = (index, field, value) => {
    setNewConfig(prev => ({
      ...prev,
      contacts: prev.contacts.map((c, i) => i === index ? { ...c, [field]: value } : c)
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage({
          file,
          preview: event.target.result,
          name: file.name
        });
        setAnalysisResult(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const analyzeImage = async () => {
    if (!uploadedImage) {
      toast({ title: "Error", description: "Please upload an image first", variant: "destructive" });
      return;
    }

    setAnalyzing(true);
    setAnalysisResult(null);

    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('file', uploadedImage.file);
      formData.append('feed_id', selectedFeed?.id || 'manual-upload');
      formData.append('location', selectedFeed?.location || 'Manual Upload');

      const response = await axios.post(`${API_URL}/api/cctv-monitoring/analyze-upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setAnalysisResult(response.data);
      
      if (response.data.alert) {
        toast({
          title: "Safety Alert Generated",
          description: response.data.alert.description,
          variant: "destructive"
        });
        // Refresh alerts
        fetchData();
      } else {
        toast({
          title: "Analysis Complete",
          description: "No safety hazards detected in this image"
        });
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast({
        title: "Analysis Failed",
        description: error.response?.data?.detail || "Failed to analyze image",
        variant: "destructive"
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const simulateLiveAnalysis = async () => {
    if (!selectedFeed) {
      toast({ title: "Error", description: "Please select a camera feed first", variant: "destructive" });
      return;
    }

    setSimulatingLive(true);
    setLiveResult(null);

    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('feed_id', selectedFeed.id);

      const response = await axios.post(`${API_URL}/api/cctv-monitoring/simulate-live-analysis`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setLiveResult(response.data);
      
      if (response.data.alert) {
        toast({
          title: "Safety Alert Generated!",
          description: response.data.alert.description,
          variant: "destructive"
        });
        // Refresh data
        fetchData();
      } else {
        toast({
          title: "All Clear",
          description: "No safety hazards detected"
        });
      }
    } catch (error) {
      console.error('Live simulation error:', error);
      toast({
        title: "Simulation Failed",
        description: error.response?.data?.detail || "Failed to run simulation",
        variant: "destructive"
      });
    } finally {
      setSimulatingLive(false);
    }
  };

  const addFeed = async () => {
    if (!newFeed.name || !newFeed.location) {
      toast({ title: "Error", description: "Name and location are required", variant: "destructive" });
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API_URL}/api/cctv-monitoring/feeds`, newFeed, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Success", description: "Camera feed added successfully" });
      setNewFeed({ name: '', location: '', description: '' });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error?.response?.status === 403 ? "Admin role required" : "Failed to add feed", variant: "destructive" });
    }
  };

  const updateAlertStatus = async (alertId, status) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API_URL}/api/cctv-monitoring/alerts/${alertId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Success", description: `Alert ${status}` });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error?.response?.status === 403 ? "Admin role required" : "Failed to update alert", variant: "destructive" });
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-black';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getAlertTypeIcon = (type) => {
    switch (type) {
      case 'ppe_violation': return HardHat;
      case 'unsafe_behavior': return Users;
      case 'hazard_detected': return AlertTriangle;
      case 'fall_risk': return Activity;
      case 'equipment_issue': return Wrench;
      default: return AlertCircle;
    }
  };

  const tabs = [
    { id: 'monitor', label: 'Live Monitor', icon: Camera },
    { id: 'analyze', label: 'Analyze Image', icon: Eye },
    { id: 'alerts', label: 'Safety Alerts', icon: Bell },
    { id: 'feeds', label: 'Camera Feeds', icon: Video },
    { id: 'settings', label: 'Alert Settings', icon: Settings }
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <Camera className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">CCTV Safety Monitoring</h1>
              <p className="text-slate-500">AI-powered workplace safety surveillance</p>
            </div>
          </div>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Camera className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.feeds?.active || 0}</p>
                  <p className="text-sm text-slate-500">Active Cameras</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.alerts?.new || 0}</p>
                  <p className="text-sm text-slate-500">New Alerts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <HardHat className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.by_type?.ppe_violation || 0}</p>
                  <p className="text-sm text-slate-500">PPE Violations</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.alerts?.resolved || 0}</p>
                  <p className="text-sm text-slate-500">Resolved</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'monitor' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Camera Feeds Grid */}
            <div className="lg:col-span-2 space-y-6">
              {/* Live Feed Viewer */}
              {selectedFeed && (
                <Card className="border-2 border-blue-500">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Video className="w-5 h-5 text-blue-600" />
                          Live Feed: {selectedFeed.name}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {selectedFeed.location}
                        </CardDescription>
                      </div>
                      {sampleImages.length > 0 && (
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant={livePlayback ? "destructive" : "outline"}
                            onClick={toggleLivePlayback}
                            data-testid="live-playback-toggle"
                          >
                            {livePlayback ? (
                              <>
                                <Pause className="w-4 h-4 mr-1" />
                                Stop
                              </>
                            ) : (
                              <>
                                <Play className="w-4 h-4 mr-1" />
                                Play Live
                              </>
                            )}
                          </Button>
                          <select 
                            value={playbackSpeed}
                            onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
                            className="text-sm border rounded-lg px-2 py-1"
                            disabled={livePlayback}
                          >
                            <option value={1}>1s</option>
                            <option value={2}>2s</option>
                            <option value={3}>3s</option>
                            <option value={5}>5s</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Live Feed Display */}
                    <div className="aspect-video bg-slate-900 rounded-xl mb-4 flex items-center justify-center relative overflow-hidden">
                      {currentFrame?.image_data ? (
                        <img
                          src={`data:image/jpeg;base64,${currentFrame.image_data}`}
                          alt="Live Feed"
                          className="w-full h-full object-contain"
                        />
                      ) : sampleImages.length > 0 ? (
                        <div className="text-center text-slate-400">
                          <Video className="w-16 h-16 mx-auto mb-2" />
                          <p>Click "Play Live" to start feed</p>
                        </div>
                      ) : (
                        <div className="text-center text-slate-400">
                          <Camera className="w-16 h-16 mx-auto mb-2" />
                          <p>No sample images uploaded</p>
                          <p className="text-sm mt-1">Upload images below to enable live feed</p>
                        </div>
                      )}
                      
                      {/* Live indicator */}
                      {livePlayback && (
                        <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 bg-red-500 rounded-full">
                          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                          <span className="text-xs text-white font-medium">LIVE</span>
                        </div>
                      )}
                      
                      {/* Frame counter */}
                      {currentFrame && (
                        <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/60 rounded text-white text-xs">
                          Frame {currentFrame.index + 1} / {currentFrame.total_images}
                        </div>
                      )}

                      {/* Timestamp */}
                      <div className="absolute bottom-3 left-3 px-2 py-1 bg-black/60 rounded text-white text-xs">
                        {new Date().toLocaleTimeString()}
                      </div>
                    </div>

                    {/* Sample Image Management */}
                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-slate-900">Sample Images</h4>
                          <p className="text-xs text-slate-500">
                            {sampleImages.length > 0 
                              ? `${sampleImages.length} image(s) uploaded - will cycle as live feed`
                              : 'Upload images to create a live feed simulation'}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => sampleUploadRef.current?.click()}
                            disabled={uploadingSamples}
                            data-testid="upload-sample-images-btn"
                          >
                            {uploadingSamples ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Plus className="w-4 h-4 mr-1" />
                                Add Images
                              </>
                            )}
                          </Button>
                          {sampleImages.length > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={clearAllSampleImages}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      <input
                        ref={sampleUploadRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={uploadSampleImages}
                        className="hidden"
                      />

                      {/* Image Thumbnails */}
                      {sampleImages.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {sampleImages.map((img, idx) => (
                            <div 
                              key={img.id}
                              className={`relative flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 ${
                                currentFrame?.image_id === img.id ? 'border-blue-500' : 'border-transparent'
                              }`}
                            >
                              <div className="w-full h-full bg-slate-300 flex items-center justify-center">
                                <span className="text-xs text-slate-600">{idx + 1}</span>
                              </div>
                              <button
                                onClick={() => deleteSampleImage(img.id)}
                                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600"
                              >
                                <XCircle className="w-3 h-3 text-white" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* AI Analysis Button for Live Feed */}
                    <div className="mt-4 flex gap-2">
                      <Button
                        onClick={analyzeCurrentFrame}
                        disabled={analyzingFrame || sampleImages.length === 0}
                        className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                        data-testid="analyze-frame-btn"
                      >
                        {analyzingFrame ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Analyzing Frame...
                          </>
                        ) : (
                          <>
                            <Eye className="w-4 h-4 mr-2" />
                            AI Analyze Current Frame
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Camera Selection Grid */}
              <Card>
                <CardHeader>
                  <CardTitle>Camera Feeds</CardTitle>
                  <CardDescription>Select a camera to view and analyze</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {feeds.map(feed => (
                      <div
                        key={feed.id}
                        onClick={() => setSelectedFeed(feed)}
                        className={`p-4 border rounded-xl cursor-pointer transition-all ${
                          selectedFeed?.id === feed.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                        }`}
                        data-testid={`camera-feed-${feed.id}`}
                      >
                        <div className="aspect-video bg-slate-200 rounded-lg mb-3 flex items-center justify-center relative overflow-hidden">
                          <Camera className="w-12 h-12 text-slate-400" />
                          <div className={`absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full ${
                            feed.feed_type === 'image_loop' ? 'bg-blue-500' : 'bg-green-500'
                          }`}>
                            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                            <span className="text-xs text-white font-medium">
                              {feed.feed_type === 'image_loop' ? 'LOOP' : 'LIVE'}
                            </span>
                          </div>
                          {feed.alert_count > 0 && (
                            <div className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                              <span className="text-xs text-white font-bold">{feed.alert_count}</span>
                            </div>
                          )}
                        </div>
                        <h3 className="font-medium text-slate-900">{feed.name}</h3>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {feed.location}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Analysis Panel */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle>Quick Analysis</CardTitle>
                  <CardDescription>Upload an image for instant safety analysis</CardDescription>
                </CardHeader>
                <CardContent>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    {uploadedImage ? (
                      <div>
                        <img
                          src={uploadedImage.preview}
                          alt="Preview"
                          className="max-h-40 mx-auto rounded-lg mb-3"
                        />
                        <p className="text-sm text-slate-600">{uploadedImage.name}</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                        <p className="text-slate-600 font-medium">Drop image or click to upload</p>
                        <p className="text-sm text-slate-400 mt-1">PNG, JPG up to 10MB</p>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  
                  <Button 
                    onClick={analyzeImage} 
                    className="w-full mt-4"
                    disabled={!uploadedImage || analyzing}
                  >
                    {analyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-2" />
                        Analyze for Hazards
                      </>
                    )}
                  </Button>

                  {/* Divider */}
                  <div className="flex items-center gap-4 my-4">
                    <div className="flex-1 border-t border-slate-200"></div>
                    <span className="text-xs text-slate-400 font-medium">OR</span>
                    <div className="flex-1 border-t border-slate-200"></div>
                  </div>

                  {/* Live Simulation Button */}
                  <Button 
                    onClick={simulateLiveAnalysis} 
                    variant="outline"
                    className="w-full border-green-500 text-green-700 hover:bg-green-50"
                    disabled={!selectedFeed || simulatingLive}
                  >
                    {simulatingLive ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Running Live Analysis...
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Simulate Live Analysis
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-slate-400 mt-2 text-center">
                    {selectedFeed ? `Analyze current frame from ${selectedFeed.name}` : 'Select a camera to simulate live analysis'}
                  </p>

                  {/* Live Analysis Result */}
                  {liveResult && (
                    <div className={`mt-4 p-4 rounded-xl ${
                      liveResult.analysis?.hazards_detected
                        ? 'bg-red-50 border border-red-200'
                        : 'bg-green-50 border border-green-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {liveResult.analysis?.hazards_detected ? (
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          )}
                          <span className={`font-medium ${
                            liveResult.analysis?.hazards_detected ? 'text-red-900' : 'text-green-900'
                          }`}>
                            {liveResult.analysis?.hazards_detected ? 'Hazards Detected!' : 'All Clear'}
                          </span>
                        </div>
                        {liveResult.simulation_mode ? (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">SIMULATION</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">🤖 GPT-4o AI</span>
                        )}
                      </div>
                      
                      {liveResult.analysis?.severity && liveResult.analysis?.hazards_detected && (
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mb-2 ${
                          liveResult.analysis.severity === 'critical' ? 'bg-red-500 text-white' :
                          liveResult.analysis.severity === 'high' ? 'bg-orange-500 text-white' :
                          liveResult.analysis.severity === 'medium' ? 'bg-yellow-500 text-black' :
                          'bg-green-500 text-white'
                        }`}>
                          {liveResult.analysis.severity.toUpperCase()}
                        </span>
                      )}
                      
                      <p className={`text-sm ${
                        liveResult.analysis?.hazards_detected ? 'text-red-700' : 'text-green-700'
                      }`}>
                        {liveResult.analysis?.description || 'Analysis complete'}
                      </p>
                      
                      {liveResult.analysis?.detected_items?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {liveResult.analysis.detected_items.map((item, i) => (
                            <span key={i} className="px-2 py-1 bg-white rounded text-xs text-slate-600">
                              {item.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {liveResult.analysis?.recommended_actions?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-red-200">
                          <p className="text-xs font-medium text-red-800 mb-1">Recommended Actions:</p>
                          <ul className="text-xs text-red-700 list-disc list-inside">
                            {liveResult.analysis.recommended_actions.map((action, i) => (
                              <li key={i}>{action}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <p className="text-xs text-slate-400 mt-3">
                        Analyzed at {new Date(liveResult.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  )}

                  {/* Analysis Result */}
                  {analysisResult && (
                    <div className={`mt-4 p-4 rounded-xl ${
                      analysisResult.analysis?.hazards_detected
                        ? 'bg-red-50 border border-red-200'
                        : 'bg-green-50 border border-green-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        {analysisResult.analysis?.hazards_detected ? (
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                        ) : (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                        <span className={`font-medium ${
                          analysisResult.analysis?.hazards_detected ? 'text-red-900' : 'text-green-900'
                        }`}>
                          {analysisResult.analysis?.hazards_detected ? 'Hazards Detected' : 'No Hazards Found'}
                        </span>
                      </div>
                      <p className={`text-sm ${
                        analysisResult.analysis?.hazards_detected ? 'text-red-700' : 'text-green-700'
                      }`}>
                        {analysisResult.analysis?.description || 'Analysis complete'}
                      </p>
                      {analysisResult.analysis?.detected_items?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {analysisResult.analysis.detected_items.map((item, i) => (
                            <span key={i} className="px-2 py-1 bg-white rounded text-xs text-slate-600">
                              {item}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Continuous Monitoring Panel */}
              <Card className="mt-4 border-purple-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg">🤖 Real-Time AI Monitoring</CardTitle>
                      {continuousMode && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                          <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></span>
                          AI ACTIVE
                        </span>
                      )}
                    </div>
                  </div>
                  <CardDescription>
                    Auto-analyze using GPT-4o AI on cycling sample images
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Sample images requirement notice */}
                  {sampleImages.length === 0 && selectedFeed && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      ⚠️ Upload sample images above to enable real-time AI monitoring
                    </div>
                  )}

                  {/* Interval Selection */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-sm text-slate-600 whitespace-nowrap">Analyze every</span>
                    <select
                      value={monitoringInterval}
                      onChange={(e) => setMonitoringInterval(Number(e.target.value))}
                      disabled={continuousMode}
                      className="px-3 py-1.5 border rounded-lg text-sm bg-white disabled:opacity-50"
                    >
                      <option value={5}>5 seconds</option>
                      <option value={10}>10 seconds</option>
                      <option value={15}>15 seconds</option>
                      <option value={30}>30 seconds</option>
                      <option value={60}>1 minute</option>
                    </select>
                  </div>

                  {/* Start/Stop Button */}
                  <Button
                    onClick={toggleContinuousMode}
                    className={`w-full ${continuousMode 
                      ? 'bg-red-500 hover:bg-red-600' 
                      : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700'
                    }`}
                    disabled={!selectedFeed || sampleImages.length === 0}
                    data-testid="continuous-monitoring-toggle"
                  >
                    {continuousMode ? (
                      <>
                        <Pause className="w-4 h-4 mr-2" />
                        Stop AI Monitoring
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Start Real-Time AI Monitoring
                      </>
                    )}
                  </Button>

                  {/* Stats when monitoring */}
                  {(continuousMode || analysisCount > 0) && (
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <div className="p-3 bg-purple-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-700">{analysisCount}</p>
                        <p className="text-xs text-purple-600">AI Analyses</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-red-700">{hazardsFound}</p>
                        <p className="text-xs text-red-600">Hazards Found</p>
                      </div>
                    </div>
                  )}

                  {/* Activity Log */}
                  {monitoringLog.length > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-slate-500">AI Analysis Log</p>
                        <span className="text-xs text-purple-600 font-medium">Real-time GPT-4o</span>
                      </div>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {monitoringLog.slice(0, 10).map((log) => (
                          <div 
                            key={log.id}
                            className={`flex items-center gap-2 p-2 rounded text-xs ${
                              log.hazardsDetected ? 'bg-red-50' : 'bg-slate-50'
                            }`}
                          >
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              log.hazardsDetected 
                                ? log.severity === 'critical' ? 'bg-red-500'
                                  : log.severity === 'high' ? 'bg-orange-500'
                                  : 'bg-yellow-500'
                                : 'bg-green-500'
                            }`}></span>
                            <span className="text-slate-500">
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </span>
                            {log.frameIndex !== undefined && (
                              <span className="text-purple-500 font-mono">
                                #{log.frameIndex + 1}
                              </span>
                            )}
                            <span className={`flex-1 truncate ${
                              log.hazardsDetected ? 'text-red-700 font-medium' : 'text-slate-600'
                            }`}>
                              {log.hazardsDetected 
                                ? `${log.alertType?.replace(/_/g, ' ') || 'Hazard'} detected`
                                : 'All clear'
                              }
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'analyze' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upload Panel */}
            <Card>
              <CardHeader>
                <CardTitle>Upload CCTV Image</CardTitle>
                <CardDescription>Upload a snapshot from your CCTV system for AI analysis</CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  {uploadedImage ? (
                    <div>
                      <img
                        src={uploadedImage.preview}
                        alt="Preview"
                        className="max-h-64 mx-auto rounded-lg mb-4"
                      />
                      <p className="text-slate-600">{uploadedImage.name}</p>
                      <p className="text-sm text-slate-400 mt-1">Click to change image</p>
                    </div>
                  ) : (
                    <>
                      <ImageIcon className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                      <p className="text-lg text-slate-600 font-medium">Drop image here or click to upload</p>
                      <p className="text-sm text-slate-400 mt-2">Supports PNG, JPG, WEBP up to 10MB</p>
                    </>
                  )}
                </div>

                <div className="mt-4">
                  <Label>Select Camera Feed (Optional)</Label>
                  <select
                    value={selectedFeed?.id || ''}
                    onChange={e => setSelectedFeed(feeds.find(f => f.id === e.target.value))}
                    className="w-full mt-1 p-2 border rounded-lg"
                  >
                    <option value="">Manual Upload</option>
                    {feeds.map(feed => (
                      <option key={feed.id} value={feed.id}>{feed.name} - {feed.location}</option>
                    ))}
                  </select>
                </div>

                <Button 
                  onClick={analyzeImage} 
                  className="w-full mt-4"
                  size="lg"
                  disabled={!uploadedImage || analyzing}
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Analyzing Image...
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5 mr-2" />
                      Run Safety Analysis
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Results Panel */}
            <Card>
              <CardHeader>
                <CardTitle>Analysis Results</CardTitle>
                <CardDescription>AI-powered safety hazard detection</CardDescription>
              </CardHeader>
              <CardContent>
                {analyzing ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-12 h-12 text-blue-600 mx-auto animate-spin mb-4" />
                    <p className="text-slate-600">AI is analyzing the image for safety hazards...</p>
                    <p className="text-sm text-slate-400 mt-2">Checking for PPE violations, unsafe behaviors, and environmental hazards</p>
                  </div>
                ) : analysisResult ? (
                  <div>
                    {/* Status Header */}
                    <div className={`p-4 rounded-xl mb-4 ${
                      analysisResult.analysis?.hazards_detected
                        ? 'bg-red-50 border border-red-200'
                        : 'bg-green-50 border border-green-200'
                    }`}>
                      <div className="flex items-center gap-3">
                        {analysisResult.analysis?.hazards_detected ? (
                          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                            <AlertTriangle className="w-6 h-6 text-red-600" />
                          </div>
                        ) : (
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          </div>
                        )}
                        <div>
                          <h3 className={`font-bold text-lg ${
                            analysisResult.analysis?.hazards_detected ? 'text-red-900' : 'text-green-900'
                          }`}>
                            {analysisResult.analysis?.hazards_detected ? 'Safety Hazards Detected' : 'All Clear'}
                          </h3>
                          <p className={`text-sm ${
                            analysisResult.analysis?.hazards_detected ? 'text-red-700' : 'text-green-700'
                          }`}>
                            Confidence: {Math.round((analysisResult.analysis?.confidence_score || 0) * 100)}%
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-500">Description</Label>
                        <p className="text-slate-900 mt-1">{analysisResult.analysis?.description}</p>
                      </div>

                      {analysisResult.analysis?.detailed_analysis && (
                        <div>
                          <Label className="text-slate-500">Detailed Analysis</Label>
                          <p className="text-slate-700 mt-1 text-sm">{analysisResult.analysis.detailed_analysis}</p>
                        </div>
                      )}

                      {analysisResult.analysis?.detected_items?.length > 0 && (
                        <div>
                          <Label className="text-slate-500">Detected Items</Label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {analysisResult.analysis.detected_items.map((item, i) => (
                              <span key={i} className="px-3 py-1 bg-slate-100 rounded-full text-sm text-slate-700">
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {analysisResult.analysis?.recommended_actions?.length > 0 && (
                        <div>
                          <Label className="text-slate-500">Recommended Actions</Label>
                          <ul className="mt-2 space-y-1">
                            {analysisResult.analysis.recommended_actions.map((action, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                                <ChevronRight className="w-4 h-4 text-blue-500 mt-0.5" />
                                {action}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <Eye className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Upload an image to see analysis results</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'alerts' && (
          <Card>
            <CardHeader>
              <CardTitle>Safety Alerts</CardTitle>
              <CardDescription>Recent safety alerts detected by AI monitoring</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {alerts.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Shield className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No safety alerts at this time</p>
                  </div>
                ) : (
                  alerts.map(alert => {
                    const AlertIcon = getAlertTypeIcon(alert.alert_type);
                    return (
                      <div
                        key={alert.id}
                        className="p-4 border rounded-xl hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            alert.severity === 'critical' ? 'bg-red-100' :
                            alert.severity === 'high' ? 'bg-orange-100' :
                            alert.severity === 'medium' ? 'bg-yellow-100' : 'bg-green-100'
                          }`}>
                            <AlertIcon className={`w-6 h-6 ${
                              alert.severity === 'critical' ? 'text-red-600' :
                              alert.severity === 'high' ? 'text-orange-600' :
                              alert.severity === 'medium' ? 'text-yellow-600' : 'text-green-600'
                            }`} />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(alert.severity)}`}>
                                {alert.severity.toUpperCase()}
                              </span>
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                {alert.alert_type.replace(/_/g, ' ')}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                alert.status === 'new' ? 'bg-blue-100 text-blue-700' :
                                alert.status === 'acknowledged' ? 'bg-yellow-100 text-yellow-700' :
                                alert.status === 'resolved' ? 'bg-green-100 text-green-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {alert.status}
                              </span>
                            </div>
                            
                            <h3 className="font-medium text-slate-900">{alert.description}</h3>
                            <p className="text-sm text-slate-500 mt-1">{alert.ai_analysis}</p>
                            
                            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Camera className="w-3 h-3" />
                                {alert.feed_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {alert.location}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(alert.timestamp).toLocaleString()}
                              </span>
                            </div>

                            {alert.recommended_action && (
                              <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                                <p className="text-sm text-blue-700">
                                  <strong>Action:</strong> {alert.recommended_action}
                                </p>
                              </div>
                            )}
                            <WsafeReviewStatus
                              sourceType="cctv_alert"
                              sourceId={alert.id}
                              onReviewChange={(review) => setAlerts(prev => prev.map(a => a.id === alert.id
                                ? { ...a, status: review === 'rejected' ? 'false_positive' : 'acknowledged' } : a))}
                            />
                          </div>

                          {alert.status === 'new' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateAlertStatus(alert.id, 'acknowledged')}
                              >
                                Acknowledge
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => updateAlertStatus(alert.id, 'resolved')}
                              >
                                Resolve
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'feeds' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Add New Feed */}
            <Card>
              <CardHeader>
                <CardTitle>Add Camera Feed</CardTitle>
                <CardDescription>Register a new CCTV camera</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Camera Name</Label>
                  <Input
                    value={newFeed.name}
                    onChange={e => setNewFeed(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g., Warehouse Camera 1"
                  />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input
                    value={newFeed.location}
                    onChange={e => setNewFeed(f => ({ ...f, location: e.target.value }))}
                    placeholder="e.g., Building A - Main Floor"
                  />
                </div>
                <div>
                  <Label>Description (Optional)</Label>
                  <Input
                    value={newFeed.description}
                    onChange={e => setNewFeed(f => ({ ...f, description: e.target.value }))}
                    placeholder="e.g., Monitors forklift operations"
                  />
                </div>
                <Button onClick={addFeed} className="w-full">
                  <Camera className="w-4 h-4 mr-2" />
                  Add Camera
                </Button>
              </CardContent>
            </Card>

            {/* Camera List */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Registered Cameras</CardTitle>
                  <CardDescription>{feeds.length} cameras registered</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {feeds.map(feed => (
                      <div key={feed.id} className="p-4 border rounded-xl">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Camera className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-slate-900">{feed.name}</h3>
                            <p className="text-sm text-slate-500">{feed.location}</p>
                          </div>
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            feed.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {feed.status}
                          </div>
                        </div>
                        {feed.description && (
                          <p className="text-sm text-slate-500 mb-3">{feed.description}</p>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-500">
                            {feed.alert_count} alerts
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedFeed(feed);
                              setActiveTab('analyze');
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Analyze
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Create New Config */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Alert Settings
                </CardTitle>
                <CardDescription>Configure proactive safety alerts</CardDescription>
              </CardHeader>
              <CardContent>
                {!showNewConfig ? (
                  <div className="text-center py-6">
                    <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">
                      Set up automatic email and in-app notifications when hazards are detected
                    </p>
                    <Button onClick={() => setShowNewConfig(true)} className="gap-2">
                      <Plus className="w-4 h-4" />
                      Create Alert Configuration
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label>Configuration Name</Label>
                      <Input
                        value={newConfig.name}
                        onChange={e => setNewConfig(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Safety Team Alerts"
                      />
                    </div>

                    <div>
                      <Label>Severity Triggers</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {['critical', 'high', 'medium', 'low'].map(sev => (
                          <button
                            key={sev}
                            onClick={() => setNewConfig(prev => ({
                              ...prev,
                              severity_triggers: prev.severity_triggers.includes(sev)
                                ? prev.severity_triggers.filter(s => s !== sev)
                                : [...prev.severity_triggers, sev]
                            }))}
                            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                              newConfig.severity_triggers.includes(sev)
                                ? sev === 'critical' ? 'bg-red-500 text-white'
                                : sev === 'high' ? 'bg-orange-500 text-white'
                                : sev === 'medium' ? 'bg-yellow-500 text-black'
                                : 'bg-green-500 text-white'
                                : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {sev}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label>Notification Channels</Label>
                      <div className="flex gap-3 mt-2">
                        <button
                          onClick={() => setNewConfig(prev => ({
                            ...prev,
                            notification_channels: prev.notification_channels.includes('email')
                              ? prev.notification_channels.filter(c => c !== 'email')
                              : [...prev.notification_channels, 'email']
                          }))}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                            newConfig.notification_channels.includes('email')
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-slate-200 text-slate-600'
                          }`}
                        >
                          <Mail className="w-4 h-4" />
                          Email
                        </button>
                        <button
                          onClick={() => setNewConfig(prev => ({
                            ...prev,
                            notification_channels: prev.notification_channels.includes('in_app')
                              ? prev.notification_channels.filter(c => c !== 'in_app')
                              : [...prev.notification_channels, 'in_app']
                          }))}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                            newConfig.notification_channels.includes('in_app')
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-slate-200 text-slate-600'
                          }`}
                        >
                          <Bell className="w-4 h-4" />
                          In-App
                        </button>
                      </div>
                    </div>

                    <div>
                      <Label>Contacts</Label>
                      <div className="space-y-2 mt-2">
                        {newConfig.contacts.map((contact, index) => (
                          <div key={index} className="flex gap-2 items-center">
                            <Input
                              placeholder="Name"
                              value={contact.name}
                              onChange={e => updateContact(index, 'name', e.target.value)}
                              className="flex-1"
                            />
                            <Input
                              placeholder="Email"
                              type="email"
                              value={contact.email}
                              onChange={e => updateContact(index, 'email', e.target.value)}
                              className="flex-1"
                            />
                            {newConfig.contacts.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeContact(index)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={addContact} className="w-full gap-2">
                          <Plus className="w-4 h-4" />
                          Add Contact
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label>Cooldown (minutes)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newConfig.cooldown_minutes}
                        onChange={e => setNewConfig(prev => ({ ...prev, cooldown_minutes: parseInt(e.target.value) || 15 }))}
                      />
                      <p className="text-xs text-slate-500 mt-1">Minimum time between alerts for the same camera</p>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button onClick={createAlertConfig} className="flex-1">
                        Create Configuration
                      </Button>
                      <Button variant="outline" onClick={() => setShowNewConfig(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Active Configurations */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Active Alert Configurations</CardTitle>
                  <CardDescription>{alertConfigs.length} configuration(s) set up</CardDescription>
                </CardHeader>
                <CardContent>
                  {alertConfigs.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <Settings className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No alert configurations yet</p>
                      <p className="text-sm">Create one to receive proactive safety alerts</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {alertConfigs.map(config => (
                        <div key={config.id} className="p-4 border rounded-xl hover:bg-slate-50 transition-colors">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => toggleAlertConfig(config.id)}
                                className={`p-1 rounded-lg ${config.enabled ? 'text-green-600' : 'text-slate-400'}`}
                              >
                                {config.enabled ? (
                                  <ToggleRight className="w-8 h-8" />
                                ) : (
                                  <ToggleLeft className="w-8 h-8" />
                                )}
                              </button>
                              <div>
                                <h3 className="font-medium text-slate-900">{config.name}</h3>
                                <p className="text-sm text-slate-500">
                                  {config.contacts?.length || 0} contact(s) • {config.cooldown_minutes}min cooldown
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => testAlertConfig(config.id)}>
                                <Send className="w-4 h-4 mr-1" />
                                Test
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteAlertConfig(config.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap gap-2 mb-3">
                            {config.severity_triggers?.map(sev => (
                              <span
                                key={sev}
                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  sev === 'critical' ? 'bg-red-100 text-red-700'
                                  : sev === 'high' ? 'bg-orange-100 text-orange-700'
                                  : sev === 'medium' ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-green-100 text-green-700'
                                }`}
                              >
                                {sev}
                              </span>
                            ))}
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            {config.notification_channels?.includes('email') && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                Email
                              </span>
                            )}
                            {config.notification_channels?.includes('in_app') && (
                              <span className="flex items-center gap-1">
                                <Bell className="w-3 h-3" />
                                In-App
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Notification History */}
              {alertNotifications.length > 0 && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <History className="w-5 h-5" />
                      Recent Notifications
                    </CardTitle>
                    <CardDescription>Last {alertNotifications.length} alert notifications sent</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {alertNotifications.slice(0, 10).map(notif => (
                        <div key={notif.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${
                              notif.severity === 'critical' ? 'bg-red-500'
                              : notif.severity === 'high' ? 'bg-orange-500'
                              : notif.severity === 'medium' ? 'bg-yellow-500'
                              : 'bg-green-500'
                            }`} />
                            <div>
                              <p className="text-sm font-medium text-slate-700">
                                {notif.feed_name} - {notif.alert_type?.replace(/_/g, ' ')}
                              </p>
                              <p className="text-xs text-slate-500">
                                {new Date(notif.created_at).toLocaleString()} • {notif.channels_notified?.join(', ')}
                              </p>
                            </div>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            notif.severity === 'critical' ? 'bg-red-100 text-red-700'
                            : notif.severity === 'high' ? 'bg-orange-100 text-orange-700'
                            : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {notif.severity}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CCTVMonitoringPage;
